import React, { useState, useEffect } from "react";
import {
  Play,
  Pause,
  RotateCcw,
  SkipForward,
  SkipBack,
  GitMerge,
  FastForward,
  Database,
} from "lucide-react";

const MergeKSortedListsVisualizer = () => {
  // Test cases
  const defaultTestCases = [
    {
      id: 1,
      name: "Three lists",
      lists: [
        [1, 4, 5],
        [1, 3, 4],
        [2, 6],
      ],
      expected: [1, 1, 2, 3, 4, 4, 5, 6],
    },
    {
      id: 2,
      name: "Empty lists",
      lists: [],
      expected: [],
    },
  ];

  const [testCases] = useState(defaultTestCases);
  const [selectedTestCase, setSelectedTestCase] = useState(0);
  const [lists, setLists] = useState(testCases[0].lists);

  // Visualization state
  const [steps, setSteps] = useState([]);
  const [currentStep, setCurrentStep] = useState(-1);
  const [isPlaying, setIsPlaying] = useState(false);
  const [speed, setSpeed] = useState(1000);
  const [finished, setFinished] = useState(false);

  // Min heap for visualization
  const [heap, setHeap] = useState([]);
  const [mergedList, setMergedList] = useState([]);

  // Pseudo code for heap approach
  const pseudoCode = [
    { line: "function mergeKLists(lists):", indent: 0 },
    { line: "minHeap = new MinHeap()", indent: 1 },
    { line: "// Add first element from each list", indent: 1 },
    { line: "for each list in lists:", indent: 1 },
    { line: "if list is not empty:", indent: 2 },
    { line: "minHeap.push({val, listIndex, nodeIndex})", indent: 3 },
    { line: "", indent: 0 },
    { line: "mergedList = []", indent: 1 },
    { line: "while minHeap is not empty:", indent: 1 },
    { line: "// Extract minimum", indent: 2 },
    { line: "min = minHeap.pop()", indent: 2 },
    { line: "mergedList.append(min.val)", indent: 2 },
    { line: "", indent: 0 },
    { line: "// Add next element from same list", indent: 2 },
    { line: "if next element exists in min.list:", indent: 2 },
    { line: "minHeap.push(nextElement)", indent: 3 },
    { line: "", indent: 0 },
    { line: "return mergedList", indent: 1 },
  ];

  const [highlightedLine, setHighlightedLine] = useState(-1);

  // MinHeap implementation for visualization
  class MinHeapNode {
    constructor(val, listIndex, nodeIndex) {
      this.val = val;
      this.listIndex = listIndex;
      this.nodeIndex = nodeIndex;
    }
  }

  // Calculate all steps
  const calculateSteps = (inputLists) => {
    if (!inputLists || inputLists.length === 0) {
      return [
        {
          step: 0,
          lists: [],
          heap: [],
          mergedList: [],
          description: "No lists to merge",
          highlightLine: 0,
          isFinal: true,
        },
      ];
    }

    const allSteps = [];
    const listsCopy = inputLists.map((list) => [...list]);
    const indices = new Array(listsCopy.length).fill(0);
    const heapArray = [];
    const merged = [];
    let step = 0;

    // Initial state
    allSteps.push({
      step: 0,
      lists: listsCopy,
      indices: [...indices],
      heap: [],
      mergedList: [],
      description: "Initialize min heap",
      highlightLine: 1,
      phase: "init",
    });

    // Add first element from each list to heap
    for (let i = 0; i < listsCopy.length; i++) {
      if (listsCopy[i].length > 0) {
        const node = {
          val: listsCopy[i][0],
          listIndex: i,
          nodeIndex: 0,
        };
        heapArray.push(node);

        // Heapify
        heapArray.sort((a, b) => a.val - b.val);

        allSteps.push({
          step: ++step,
          lists: listsCopy,
          indices: [...indices],
          heap: [...heapArray],
          mergedList: [...merged],
          description: `Add first element (${node.val}) from list ${
            i + 1
          } to heap`,
          highlightLine: 5,
          phase: "add-initial",
          highlightedList: i,
          addedToHeap: node,
        });
      }
    }

    // Process heap
    while (heapArray.length > 0) {
      // Show heap state
      allSteps.push({
        step: ++step,
        lists: listsCopy,
        indices: [...indices],
        heap: [...heapArray],
        mergedList: [...merged],
        description: `Min heap contains ${heapArray.length} elements. Minimum: ${heapArray[0].val}`,
        highlightLine: 10,
        phase: "heap-check",
        heapMin: heapArray[0],
      });

      // Extract minimum
      const min = heapArray.shift();
      merged.push(min.val);
      indices[min.listIndex]++;

      allSteps.push({
        step: ++step,
        lists: listsCopy,
        indices: [...indices],
        heap: [...heapArray],
        mergedList: [...merged],
        description: `Extract minimum (${min.val}) from list ${
          min.listIndex + 1
        } and add to merged list`,
        highlightLine: 11,
        phase: "extract-min",
        extractedNode: min,
        mergedHighlight: merged.length - 1,
      });

      // Add next element from the same list if exists
      if (indices[min.listIndex] < listsCopy[min.listIndex].length) {
        const nextNode = {
          val: listsCopy[min.listIndex][indices[min.listIndex]],
          listIndex: min.listIndex,
          nodeIndex: indices[min.listIndex],
        };

        heapArray.push(nextNode);
        heapArray.sort((a, b) => a.val - b.val);

        allSteps.push({
          step: ++step,
          lists: listsCopy,
          indices: [...indices],
          heap: [...heapArray],
          mergedList: [...merged],
          description: `Add next element (${nextNode.val}) from list ${
            min.listIndex + 1
          } to heap`,
          highlightLine: 15,
          phase: "add-next",
          highlightedList: min.listIndex,
          addedToHeap: nextNode,
        });
      }
    }

    // Final state
    allSteps.push({
      step: ++step,
      lists: listsCopy,
      indices: [...indices],
      heap: [],
      mergedList: [...merged],
      description: `Merge complete! Final list: [${merged.join(", ")}]`,
      highlightLine: 17,
      phase: "complete",
      isFinal: true,
    });

    return allSteps;
  };

  // Initialize steps when test case changes
  useEffect(() => {
    const newSteps = calculateSteps(lists);
    setSteps(newSteps);
    setCurrentStep(0);
    setFinished(false);
  }, [lists]);

  // Auto-play functionality
  useEffect(() => {
    if (!isPlaying || finished) return;

    const timer = setTimeout(() => {
      handleNextStep();
    }, speed);

    return () => clearTimeout(timer);
  }, [isPlaying, currentStep, finished, speed]);

  const handleNextStep = () => {
    if (currentStep < steps.length - 1) {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      const step = steps[nextStep];
      setHighlightedLine(step.highlightLine);

      if (step.isFinal) {
        setFinished(true);
        setIsPlaying(false);
      }
    }
  };

  const handlePreviousStep = () => {
    if (currentStep > 0) {
      const prevStep = currentStep - 1;
      setCurrentStep(prevStep);
      const step = steps[prevStep];
      setHighlightedLine(step.highlightLine);
      setFinished(false);
    }
  };

  const reset = () => {
    setCurrentStep(0);
    setIsPlaying(false);
    setHighlightedLine(-1);
    setFinished(false);
  };

  const handleTestCaseChange = (index) => {
    setSelectedTestCase(index);
    setLists(testCases[index].lists);
  };

  const getSpeedLabel = () => {
    if (speed <= 500) return "Fast";
    if (speed <= 1000) return "Normal";
    return "Slow";
  };

  const currentStepData = steps[currentStep] || steps[0];

  return (
    <div className="p-4 max-w-7xl mx-auto">
      <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
        <GitMerge className="text-blue-500" size={24} />
        Merge k Sorted Lists - Min Heap Approach
      </h2>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Left side - Visualization */}
        <div className="space-y-3">
          {/* Test Cases */}
          <div className="bg-white p-3 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2">Test Cases</h3>
            <div className="space-y-1">
              {testCases.map((tc, idx) => (
                <button
                  key={tc.id}
                  onClick={() => handleTestCaseChange(idx)}
                  className={`w-full px-2 py-1.5 text-left rounded transition-colors ${
                    selectedTestCase === idx
                      ? "bg-blue-500 text-white"
                      : "bg-gray-100 hover:bg-gray-200"
                  }`}
                >
                  <div className="text-xs font-medium">{tc.name}</div>
                  <div className="text-xs opacity-75">
                    {tc.lists.length > 0
                      ? `${tc.lists.length} lists: ${tc.lists
                          .map((l) => `[${l.join(",")}]`)
                          .join(" ")}`
                      : "No lists"}
                  </div>
                  <div className="text-xs opacity-75">
                    Output: [{tc.expected.join(", ")}]
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Lists Visualization */}
          <div className="bg-white p-3 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2">
              Step {currentStep}: {currentStepData?.description}
            </h3>

            {/* Input Lists */}
            <div className="mb-3">
              <p className="text-xs font-medium text-gray-700 mb-2">
                Input Lists:
              </p>
              <div className="space-y-2">
                {currentStepData?.lists.length > 0 ? (
                  currentStepData.lists.map((list, listIdx) => (
                    <div
                      key={listIdx}
                      className={`p-2 rounded transition-all ${
                        currentStepData.highlightedList === listIdx
                          ? "bg-yellow-50 border border-yellow-300"
                          : "bg-gray-50"
                      }`}
                    >
                      <p className="text-xs text-gray-600 mb-1">
                        List {listIdx + 1}:
                      </p>
                      <div className="flex gap-1 flex-wrap">
                        {list.map((val, nodeIdx) => (
                          <div
                            key={nodeIdx}
                            className={`w-10 h-10 flex items-center justify-center border-2 rounded text-sm font-mono font-bold transition-all ${
                              nodeIdx <
                              (currentStepData.indices?.[listIdx] || 0)
                                ? "bg-gray-200 border-gray-400 opacity-50"
                                : nodeIdx ===
                                  (currentStepData.indices?.[listIdx] || 0)
                                ? "bg-blue-200 border-blue-500 scale-110"
                                : "bg-white border-gray-300"
                            }`}
                          >
                            {val}
                          </div>
                        ))}
                        {list.length === 0 && (
                          <span className="text-xs text-gray-400 italic">
                            Empty
                          </span>
                        )}
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-xs text-gray-400 italic">
                    No lists to merge
                  </p>
                )}
              </div>
            </div>

            {/* Min Heap */}
            <div className="mb-3 p-2 bg-purple-50 rounded">
              <p className="text-xs font-medium text-purple-700 mb-2 flex items-center gap-1">
                <Database size={14} />
                Min Heap:
              </p>
              <div className="flex gap-1 flex-wrap">
                {currentStepData?.heap?.length > 0 ? (
                  currentStepData.heap.map((node, idx) => (
                    <div
                      key={idx}
                      className={`relative p-2 rounded border-2 transition-all ${
                        idx === 0
                          ? "bg-purple-200 border-purple-500 scale-110"
                          : "bg-purple-100 border-purple-300"
                      }`}
                    >
                      <div className="text-sm font-mono font-bold">
                        {node.val}
                      </div>
                      <div className="text-xs text-purple-600">
                        L{node.listIndex + 1}
                      </div>
                    </div>
                  ))
                ) : (
                  <span className="text-xs text-purple-600 italic">
                    Empty heap
                  </span>
                )}
              </div>
              {currentStepData?.heapMin && (
                <p className="text-xs text-purple-700 mt-2">
                  Min element: {currentStepData.heapMin.val} from List{" "}
                  {currentStepData.heapMin.listIndex + 1}
                </p>
              )}
            </div>

            {/* Merged List */}
            <div className="p-2 bg-green-50 rounded">
              <p className="text-xs font-medium text-green-700 mb-2">
                Merged List:
              </p>
              <div className="flex gap-1 flex-wrap">
                {currentStepData?.mergedList?.length > 0 ? (
                  currentStepData.mergedList.map((val, idx) => (
                    <div
                      key={idx}
                      className={`w-10 h-10 flex items-center justify-center border-2 rounded text-sm font-mono font-bold transition-all ${
                        idx === currentStepData.mergedHighlight
                          ? "bg-green-200 border-green-500 scale-110 animate-pulse"
                          : "bg-green-100 border-green-300"
                      }`}
                    >
                      {val}
                    </div>
                  ))
                ) : (
                  <span className="text-xs text-green-600 italic">Empty</span>
                )}
              </div>
            </div>

            {/* Result */}
            {currentStepData?.isFinal && (
              <div className="mt-3 p-3 bg-blue-100 rounded">
                <h4 className="text-sm font-bold text-blue-900">
                  Merge Complete!
                </h4>
                <p className="text-xs text-blue-700 mt-1">
                  Merged {currentStepData.lists.length} lists into one sorted
                  list
                </p>
                <p className="text-xs text-blue-700">
                  Total elements: {currentStepData.mergedList.length}
                </p>
              </div>
            )}
          </div>

          {/* Controls */}
          <div className="bg-white p-3 rounded-lg shadow">
            <div className="flex items-center gap-2">
              <button
                onClick={handlePreviousStep}
                disabled={currentStep === 0}
                className="p-1.5 bg-gray-500 text-white rounded hover:bg-gray-600 disabled:bg-gray-300"
              >
                <SkipBack size={14} />
              </button>

              <button
                onClick={() => setIsPlaying(!isPlaying)}
                disabled={finished}
                className="px-3 py-1.5 text-xs bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-gray-300 flex items-center gap-1"
              >
                {isPlaying ? <Pause size={14} /> : <Play size={14} />}
                {isPlaying ? "Pause" : "Play"}
              </button>

              <button
                onClick={handleNextStep}
                disabled={currentStep === steps.length - 1}
                className="p-1.5 bg-gray-500 text-white rounded hover:bg-gray-600 disabled:bg-gray-300"
              >
                <SkipForward size={14} />
              </button>

              <button
                onClick={reset}
                className="px-3 py-1.5 text-xs bg-gray-500 text-white rounded hover:bg-gray-600 flex items-center gap-1"
              >
                <RotateCcw size={14} />
                Reset
              </button>

              <div className="flex items-center gap-1 ml-auto">
                <FastForward size={14} className="text-gray-600" />
                <label className="text-xs text-gray-600">Speed:</label>
                <input
                  type="range"
                  min="200"
                  max="2000"
                  step="200"
                  value={2200 - speed}
                  onChange={(e) => setSpeed(2200 - parseInt(e.target.value))}
                  className="w-20"
                />
                <span className="text-xs font-medium w-10">
                  {getSpeedLabel()}
                </span>
              </div>
            </div>

            {/* Step indicator */}
            <div className="mt-2 flex items-center justify-center gap-0.5">
              {steps.map((_, idx) => (
                <div
                  key={idx}
                  className={`w-1.5 h-1.5 rounded-full transition-all ${
                    idx === currentStep
                      ? "bg-blue-500 w-6"
                      : idx < currentStep
                      ? "bg-blue-300"
                      : "bg-gray-300"
                  }`}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Right side - Pseudo Code & Analysis */}
        <div className="space-y-3">
          <div className="bg-gray-900 text-gray-100 p-4 rounded-lg shadow font-mono text-xs">
            <h3 className="text-sm font-semibold mb-3 text-white">
              Min Heap Algorithm
            </h3>
            <div className="space-y-0.5">
              {pseudoCode.map((line, idx) => (
                <div
                  key={idx}
                  className={`py-0.5 px-2 rounded transition-all duration-300 ${
                    idx === highlightedLine
                      ? "bg-yellow-500 bg-opacity-30 text-yellow-200"
                      : ""
                  }`}
                  style={{ paddingLeft: `${line.indent * 16 + 8}px` }}
                >
                  {line.line}
                </div>
              ))}
            </div>
          </div>

          {/* Complexity Analysis */}
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2">Complexity Analysis</h3>
            <div className="space-y-2">
              <div>
                <h4 className="text-xs font-medium text-gray-700">
                  Time Complexity: O(N log k)
                </h4>
                <p className="text-xs text-gray-600">
                  N = total elements, k = number of lists. Each heap operation
                  is O(log k).
                </p>
              </div>
              <div>
                <h4 className="text-xs font-medium text-gray-700">
                  Space Complexity: O(k)
                </h4>
                <p className="text-xs text-gray-600">
                  Heap contains at most k elements (one from each list).
                </p>
              </div>
            </div>
          </div>

          {/* Algorithm Explanation */}
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2">How It Works</h3>
            <ol className="space-y-1 text-xs text-gray-700">
              <li className="flex">
                <span className="font-medium mr-1">1.</span>
                <span>Create a min heap to track smallest elements</span>
              </li>
              <li className="flex">
                <span className="font-medium mr-1">2.</span>
                <span>Add the first element from each list to heap</span>
              </li>
              <li className="flex">
                <span className="font-medium mr-1">3.</span>
                <span>Extract minimum element from heap</span>
              </li>
              <li className="flex">
                <span className="font-medium mr-1">4.</span>
                <span>Add extracted element to merged list</span>
              </li>
              <li className="flex">
                <span className="font-medium mr-1">5.</span>
                <span>Add next element from same list to heap (if exists)</span>
              </li>
              <li className="flex">
                <span className="font-medium mr-1">6.</span>
                <span>Repeat until heap is empty</span>
              </li>
            </ol>
          </div>

          {/* Alternative Approaches */}
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2">
              Alternative Approaches
            </h3>
            <div className="space-y-2 text-xs">
              <div className="p-2 bg-gray-50 rounded">
                <h4 className="font-medium text-gray-700">
                  1. Merge Lists One by One
                </h4>
                <p className="text-gray-600">Time: O(Nk), Space: O(1)</p>
                <p className="text-gray-600 text-xs">
                  Merge first two, then merge result with third, etc.
                </p>
              </div>
              <div className="p-2 bg-gray-50 rounded">
                <h4 className="font-medium text-gray-700">
                  2. Divide and Conquer
                </h4>
                <p className="text-gray-600">
                  Time: O(N log k), Space: O(log k)
                </p>
                <p className="text-gray-600 text-xs">
                  Merge lists in pairs recursively.
                </p>
              </div>
              <div className="p-2 bg-blue-50 rounded">
                <h4 className="font-medium text-blue-700">
                  3. Min Heap (Current)
                </h4>
                <p className="text-blue-600">Time: O(N log k), Space: O(k)</p>
                <p className="text-blue-600 text-xs">
                  Most efficient for large k.
                </p>
              </div>
            </div>
          </div>

          {/* Key Insights */}
          <div className="bg-blue-50 p-4 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2 text-blue-900">
              Key Insights
            </h3>
            <ul className="space-y-1 text-xs text-blue-800">
              <li className="flex">
                <span className="mr-1">💡</span>
                <span>Min heap ensures we always get the global minimum</span>
              </li>
              <li className="flex">
                <span className="mr-1">💡</span>
                <span>Heap size never exceeds k, keeping memory usage low</span>
              </li>
              <li className="flex">
                <span className="mr-1">💡</span>
                <span>
                  Each element is added and removed from heap exactly once
                </span>
              </li>
              <li className="flex">
                <span className="mr-1">💡</span>
                <span>
                  Works efficiently even with lists of different lengths
                </span>
              </li>
              <li className="flex">
                <span className="mr-1">💡</span>
                <span>
                  Heap maintains ordering without comparing all elements
                </span>
              </li>
            </ul>
          </div>

          {/* Visual Guide */}
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2">Visual Guide</h3>
            <div className="space-y-2 text-xs">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-blue-200 border-2 border-blue-500 rounded"></div>
                <span>Current element being processed</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-purple-200 border-2 border-purple-500 rounded"></div>
                <span>Minimum element in heap</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-green-200 border-2 border-green-500 rounded"></div>
                <span>Just added to merged list</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-gray-200 border-2 border-gray-400 rounded opacity-50"></div>
                <span>Already processed</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="p-2 bg-yellow-50 border border-yellow-300 rounded"></div>
                <span>Currently active list</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MergeKSortedListsVisualizer;
